import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
} from '@mui/material';
import {
  Close,
  Search,
  Gavel,
  ExpandMore,
  ExpandLess,
  Add,
  Refresh,
} from '@mui/icons-material';
import {
  searchJurisprudence,
  listCourts,
  listLegalDomains,
  type JurisprudenceSearchResult,
} from '../../services/jurisprudenceService';

interface IntegratedJurisprudencePanelProps {
  documentContent: string;
  documentTitle: string;
  isVisible: boolean;
  onClose: () => void;
  onInsertJurisprudence: (jurisprudence: JurisprudenceSearchResult) => void;
  selectedTribunals?: string[];
  documentArea?: string;
}

const IntegratedJurisprudencePanel: React.FC<IntegratedJurisprudencePanelProps> = ({
  isVisible,
  onClose,
  onInsertJurisprudence,
  documentArea,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<JurisprudenceSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [courtFilter, setCourtFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState(documentArea || '');
  const [courts, setCourts] = useState<string[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const itemsPerPage = 5;

  useEffect(() => {
    if (isVisible) {
      loadFilters();
      if (documentArea) {
        setDomainFilter(documentArea);
      }
    }
  }, [isVisible, documentArea]);

  const loadFilters = async () => {
    try {
      const [courtsData, domainsData] = await Promise.all([
        listCourts(),
        listLegalDomains(),
      ]);
      setCourts(courtsData);
      setDomains(domainsData);
    } catch (err) {
      console.error('Erro ao carregar filtros:', err);
    }
  };

  const handleSearch = async () => {
    if (searchTerm.length < 3) {
      setError('Digite pelo menos 3 caracteres para buscar');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await searchJurisprudence(searchTerm, {
        court_name: courtFilter || undefined,
        legal_domain: domainFilter || undefined,
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
      });
      setResults(data.items);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao buscar jurisprudências');
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = (item: JurisprudenceSearchResult) => {
    onInsertJurisprudence(item);
    setExpandedItem(null);
    // Fechar o painel após inserir
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  useEffect(() => {
    if (searchTerm.length >= 3) {
      const debounce = setTimeout(() => {
        handleSearch();
      }, 500);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm, courtFilter, domainFilter, page]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <Drawer
      anchor="right"
      open={isVisible}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          p: 2,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Gavel sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6">Jurisprudências Validadas</Typography>
              <Typography variant="caption" color="text.secondary">
                Base de dados real • Sem alucinação
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Alert severity="success" icon={<Gavel />} sx={{ mb: 2 }}>
          <Typography variant="caption">
            <strong>571 jurisprudências</strong> validadas por advogados especialistas
          </Typography>
        </Alert>

        <Divider sx={{ mb: 2 }} />

        {/* Busca */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Buscar jurisprudências"
            placeholder="Ex: IPTU, tributário, execução..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && searchTerm.length >= 3) {
                handleSearch();
              }
            }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: searchTerm && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    setResults([]);
                  }}
                >
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              ),
            }}
            sx={{ mb: 2 }}
            helperText={
              searchTerm.length > 0 && searchTerm.length < 3
                ? "Digite pelo menos 3 caracteres"
                : `${results.length > 0 ? `${total} encontradas` : 'Digite para buscar'}`
            }
            error={searchTerm.length > 0 && searchTerm.length < 3}
          />

          <Stack spacing={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>🏛️ Tribunal</InputLabel>
              <Select
                value={courtFilter}
                label="🏛️ Tribunal"
                onChange={(e) => setCourtFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Todos os Tribunais</em>
                </MenuItem>
                {courts.map((court) => (
                  <MenuItem key={court} value={court}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Gavel sx={{ fontSize: 16 }} />
                      {court}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {domains.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel>📚 Área do Direito</InputLabel>
                <Select
                  value={domainFilter}
                  label="📚 Área do Direito"
                  onChange={(e) => setDomainFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Todas as Áreas</em>
                  </MenuItem>
                  {domains.map((domain) => (
                    <MenuItem key={domain} value={domain}>
                      {domain}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(courtFilter || domainFilter) && (
              <Button
                size="small"
                variant="outlined"
                fullWidth
                startIcon={<Refresh />}
                onClick={() => {
                  setCourtFilter('');
                  setDomainFilter('');
                }}
              >
                Limpar Filtros
              </Button>
            )}
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Mensagens */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {searchTerm.length === 0 && !loading && results.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', mb: 2 }}>
            <Gavel sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Busque Jurisprudências Validadas
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Digite palavras-chave como "IPTU", "tributário", "execução", etc. 
              Todos os resultados são jurisprudências reais do banco de dados.
            </Typography>
          </Paper>
        )}

        {searchTerm.length > 0 && searchTerm.length < 3 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Digite pelo menos 3 caracteres para buscar
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Resultados */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {!loading && results.length === 0 && searchTerm.length >= 3 && (
            <Alert severity="info">Nenhuma jurisprudência encontrada</Alert>
          )}

          {!loading && results.length > 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption">
                  <strong>{total} jurisprudência{total !== 1 ? 's' : ''}</strong> encontrada{total !== 1 ? 's' : ''} • Todas validadas
                </Typography>
              </Alert>

              <List sx={{ p: 0 }}>
                {results.map((item) => (
                  <Paper 
                    key={item.id} 
                    elevation={2} 
                    sx={{ 
                      mb: 2,
                      border: 1,
                      borderColor: 'success.light',
                      '&:hover': {
                        borderColor: 'success.main',
                        boxShadow: 3
                      }
                    }}
                  >
                    <ListItem
                      button
                      onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                      sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem', mb: 0.5 }}>
                            {item.title.length > 60 ? item.title.substring(0, 60) + '...' : item.title}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, mb: 1, flexWrap: 'wrap' }} useFlexGap>
                            <Chip 
                              icon={<Gavel sx={{ fontSize: '0.8rem' }} />}
                              label={item.court_name} 
                              size="small" 
                              color="primary" 
                              sx={{ height: 22, fontSize: '0.7rem' }} 
                            />
                            {item.legal_domain && (
                              <Chip label={item.legal_domain} size="small" color="secondary" sx={{ height: 22, fontSize: '0.7rem' }} />
                            )}
                            {item.judgment_date && (
                              <Chip 
                                label={formatDate(item.judgment_date)} 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.7rem' }} 
                              />
                            )}
                          </Stack>
                        </Box>
                        <IconButton size="small">
                          {expandedItem === item.id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          display: '-webkit-box', 
                          WebkitLineClamp: 2, 
                          WebkitBoxOrient: 'vertical', 
                          overflow: 'hidden',
                          lineHeight: 1.4
                        }}
                      >
                        {item.ementa}
                      </Typography>
                    </ListItem>

                    <Collapse in={expandedItem === item.id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'success.50' }}>
                        {item.cnj_number && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            <strong>CNJ:</strong> {item.cnj_number}
                          </Typography>
                        )}
                        
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'success.dark' }}>
                          📋 Ementa:
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                          {item.ementa}
                        </Typography>

                        {item.binding_thesis && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                ⚖️ Tese Vinculante:
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                                {item.binding_thesis}
                              </Typography>
                            </Alert>
                          </>
                        )}

                        {item.summary && (
                          <>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'info.dark' }}>
                              💡 Resumo:
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, whiteSpace: 'pre-wrap' }}>
                              {item.summary}
                            </Typography>
                          </>
                        )}

                        <Divider sx={{ my: 1.5 }} />

                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          startIcon={<Add />}
                          onClick={() => handleInsert(item)}
                          color="success"
                        >
                          Inserir no Documento
                        </Button>
                      </Box>
                    </Collapse>
                  </Paper>
                ))}
              </List>
            </>
          )}
        </Box>

        {/* Paginação */}
        {total > itemsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Pagination
              count={Math.ceil(total / itemsPerPage)}
              page={page}
              onChange={handlePageChange}
              size="small"
              color="primary"
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default IntegratedJurisprudencePanel;
